import { MonsterAI, type SettlementGrid, type Inventory } from '@slimebound/core';
import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type AbstractMesh,
  type ShadowGenerator
} from '@babylonjs/core';
import { MonsterMesh } from '../npc/monster-mesh.js';

export class SettlementManager {
  public grid: SettlementGrid;
  public inventory: Inventory;
  private onLog?: (msg: string) => void;
  private monsters: MonsterAI[] = [];
  private monsterMeshes = new Map<string, MonsterMesh>();
  private structureMeshes = new Map<string, AbstractMesh>();

  constructor(
    grid: SettlementGrid,
    inventory: Inventory,
    onLog?: (msg: string) => void
  ) {
    this.grid = grid;
    this.inventory = inventory;
    this.onLog = onLog;
  }

  public addMonster(monster: MonsterAI, mesh?: MonsterMesh): void {
    this.monsters.push(monster);
    if (mesh) {
      this.monsterMeshes.set(monster.id, mesh);
    }
  }

  public getMonsters(): readonly MonsterAI[] {
    return this.monsters;
  }

  public interactFeedMonster(monsterId: string): boolean {
    const monster = this.monsters.find((m) => m.id === monsterId);
    if (!monster) return false;

    // Priority: gel_salve (yields 40 affinity), then herb_vitality (yields 20 affinity)
    let foodItem: { itemId: string; name: string; gain: number } | null = null;
    if (this.inventory.hasItem('gel_salve', 1)) {
      foodItem = { itemId: 'gel_salve', name: 'Gel Salve', gain: 40 };
    } else if (this.inventory.hasItem('herb_vitality', 1)) {
      foodItem = { itemId: 'herb_vitality', name: 'Vitality Herb', gain: 20 };
    }

    if (!foodItem) {
      return false;
    }

    this.inventory.removeItem(foodItem.itemId, 1);
    const result = monster.feed({
      itemId: foodItem.itemId,
      name: foodItem.name,
      affinityGain: foodItem.gain
    });

    if (result.tamed) {
      monster.assignJob('foraging');
      if (this.onLog) {
        this.onLog(`🎉 Tamed ${monster.name}! They joined your camp as a Forager!`);
      }
    } else if (this.onLog) {
      this.onLog(
        `Fed ${monster.name} ${foodItem.name} (+${foodItem.gain} Affinity: ${result.currentAffinity}/${monster.affinityThreshold})`
      );
    }

    return true;
  }

  public spawnCampWorld(scene: Scene, shadowGenerator?: ShadowGenerator): void {
    // Build central campfire at (0, 0)
    const campfireMat = new StandardMaterial('campfireMat', scene);
    campfireMat.diffuseColor = new Color3(0.85, 0.35, 0.1);
    campfireMat.emissiveColor = new Color3(0.6, 0.2, 0.05);

    const stoneRingMat = new StandardMaterial('stoneRingMat', scene);
    stoneRingMat.diffuseColor = new Color3(0.25, 0.25, 0.28);

    const campfireMesh = MeshBuilder.CreateCylinder(
      'campfire_core',
      { height: 0.4, diameter: 1.2 },
      scene
    );
    campfireMesh.position.set(0, 0.2, 0);
    campfireMesh.material = campfireMat;

    const ring = MeshBuilder.CreateTorus(
      'campfire_ring',
      { diameter: 1.6, thickness: 0.35, tessellation: 16 },
      scene
    );
    ring.position.set(0, 0.15, 0);
    ring.material = stoneRingMat;

    if (shadowGenerator) {
      shadowGenerator.addShadowCaster(campfireMesh);
      shadowGenerator.addShadowCaster(ring);
    }

    this.structureMeshes.set('campfire', campfireMesh);

    // Spawn companion horned bunny near campfire
    const bunny = new MonsterAI({
      id: 'companion_bunny_1',
      name: 'Horned Bunny',
      species: 'horned_bunny',
      affinityThreshold: 30,
      position: { x: 3.0, y: 0, z: 2.5 }
    });
    const bunnyMesh = new MonsterMesh(scene, bunny.id, shadowGenerator);
    bunnyMesh.setPosition(bunny.position.x, bunny.position.y, bunny.position.z);
    this.addMonster(bunny, bunnyMesh);
  }

  public checkMonsterProximity(
    slimePos: { x: number; y: number; z: number },
    slimeRadius: number
  ): MonsterAI | null {
    for (const monster of this.monsters) {
      const dx = slimePos.x - monster.position.x;
      const dz = slimePos.z - monster.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist <= slimeRadius + 1.8) {
        return monster;
      }
    }
    return null;
  }

  public update(dt: number): void {
    for (const monster of this.monsters) {
      const updateResult = monster.update(dt);
      if (updateResult.yieldItem) {
        this.inventory.addItem({
          itemId: updateResult.yieldItem.itemId,
          name: updateResult.yieldItem.name,
          quantity: updateResult.yieldItem.quantity
        });
        if (this.onLog) {
          this.onLog(
            `📦 ${monster.name} foraged +${updateResult.yieldItem.quantity} ${updateResult.yieldItem.name}!`
          );
        }
      }

      const mesh = this.monsterMeshes.get(monster.id);
      if (mesh) {
        mesh.update(dt, monster.job === 'foraging');
      }
    }
  }
}
